/**
 * MinimapRenderer — Bottom-right 150x150 canvas that shows a bird's-eye
 * view of the arena with dots for all entities.
 */
import { GameMode, GamePhase } from '../../shared/types.js';

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface MinimapEntity {
  position: Vec3;
  color: string;
  size: number;
}

export interface MinimapZoneState {
  mode?: string;
  phase?: string;
  zoneRadius?: number;
  zoneCenterX?: number;
  zoneCenterZ?: number;
}

const MAP_SIZE = 150; // canvas px

export class MinimapRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private arenaW: number;
  private arenaD: number;
  private container: HTMLElement;

  constructor(
    parentElement: HTMLElement,
    arenaWidth: number = 200,
    arenaDepth: number = 200,
  ) {
    this.arenaW = arenaWidth;
    this.arenaD = arenaDepth;

    this.container = document.createElement('div');
    this.container.className = 'hud-panel minimap hidden';

    this.canvas = document.createElement('canvas');
    this.canvas.width = MAP_SIZE;
    this.canvas.height = MAP_SIZE;
    this.container.appendChild(this.canvas);

    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('Cannot get 2d context for minimap');
    this.ctx = ctx;

    parentElement.appendChild(this.container);
  }

  /**
   * Redraw every frame.
   * @param entities All dots to render.
   * @param cameraCenter Current camera/player world position.
   * @param state Optional room state. When `state.mode === 'br'` and
   *              `state.phase === 'playing'`, a cyan safe-zone ring is drawn.
   */
  update(
    entities: MinimapEntity[],
    cameraCenter: Vec3,
    state?: MinimapZoneState | null,
    cameraOrbitAngle: number = 0,
  ): void {
    const ctx = this.ctx;
    const w = MAP_SIZE;
    const h = MAP_SIZE;

    ctx.clearRect(0, 0, w, h);

    ctx.strokeStyle = 'rgba(0, 200, 255, 0.2)';
    ctx.lineWidth = 1;
    ctx.strokeRect(1, 1, w - 2, h - 2);

    ctx.strokeStyle = 'rgba(0, 200, 255, 0.05)';
    ctx.lineWidth = 0.5;
    for (let i = 1; i < 4; i++) {
      const x = (w / 4) * i;
      const y = (h / 4) * i;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }

    // BR safe-zone ring (drawn under entity dots so drones remain visible).
    // Pass through unclamped coords — the ring legitimately straddles the
    // canvas edge when the camera is near the zone boundary.
    if (state?.mode === GameMode.Br && state.phase === GamePhase.Playing) {
      const zoneCenter = this.worldToCameraUnclamped(
        { x: state.zoneCenterX ?? 0, y: 0, z: state.zoneCenterZ ?? 0 },
        cameraCenter,
        cameraOrbitAngle,
        w,
        h,
      );
      const zpx = ((state.zoneRadius ?? 0) / (this.arenaW / 2)) * (w / 2);
      if (zpx > 0) {
        ctx.strokeStyle = 'rgba(0, 255, 200, 0.7)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(zoneCenter.x, zoneCenter.y, zpx, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    for (const e of entities) {
      const mapped = this.worldToCameraMap(e.position, cameraCenter, cameraOrbitAngle, w, h);
      if (!mapped) continue;
      ctx.fillStyle = e.color;
      ctx.beginPath();
      ctx.arc(mapped.x, mapped.y, e.size, 0, Math.PI * 2);
      ctx.fill();
    }

    const cx = w / 2;
    const cz = h / 2;

    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(cx, cz - 4);
    ctx.lineTo(cx + 3, cz);
    ctx.lineTo(cx, cz + 4);
    ctx.lineTo(cx - 3, cz);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = 'rgba(0, 200, 255, 0.6)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  show(): void {
    this.container.classList.remove('hidden');
  }

  hide(): void {
    this.container.classList.add('hidden');
  }

  private worldToCameraUnclamped(
    position: Vec3,
    center: Vec3,
    cameraOrbitAngle: number,
    canvasW: number,
    canvasH: number,
  ): { x: number; y: number } {
    const dx = position.x - center.x;
    const dz = position.z - center.z;
    const forwardX = -Math.cos(cameraOrbitAngle);
    const forwardZ = -Math.sin(cameraOrbitAngle);
    const rightX = -forwardZ;
    const rightZ = forwardX;
    const scale = Math.min(canvasW / this.arenaW, canvasH / this.arenaD);

    return {
      x: canvasW / 2 + (dx * rightX + dz * rightZ) * scale,
      y: canvasH / 2 - (dx * forwardX + dz * forwardZ) * scale,
    };
  }

  /**
   * Project world position to canvas pixels and return null when it falls
   * outside the visible minimap. Drones outside the radar should disappear,
   * not pile up on the border edge — that hides cluster information in BR
   * mode (an enemy push from one direction collapses to a thick band).
   */
  private worldToCameraMap(
    position: Vec3,
    center: Vec3,
    cameraOrbitAngle: number,
    canvasW: number,
    canvasH: number,
  ): { x: number; y: number } | null {
    const mapped = this.worldToCameraUnclamped(
      position,
      center,
      cameraOrbitAngle,
      canvasW,
      canvasH,
    );
    if (mapped.x < 0 || mapped.x > canvasW || mapped.y < 0 || mapped.y > canvasH) return null;
    return mapped;
  }
}
